import React, { useState, useEffect, useMemo } from 'react';
import StatsCards from './StatsCard';
import JobList from './RecentJobList';
import ApplicationList from '../Applications/ApplicationList';
import JobDescriptionView from '../MyJobs/JobDescriptionView';
import useJob from '../../Hooks/jobHook';
import { useSearchParams } from 'react-router-dom';
import { getCachedOverviewStats } from './overviewStatsCache';
// AI Assistant — publish the overview stats to the dock.
import { useAssistantPage } from '../Applications/copilot/useAssistantPage';

// Single job fetch for entire Overview: one get_jd request, shared by stats + recent list
function JobDashboard({ onViewChange }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [viewingJobDescription, setViewingJobDescription] = useState(null);
  const { jobs, loading: jobsLoading, refreshJobs, togglePinJob, deleteJob } = useJob();
  const recentJobs = jobs.slice(0, 5);
  const view = searchParams.get('view');
  const jobIdFromUrl = searchParams.get('jobId');

  // Keep applications view in sync with URL so refresh/back works.
  useEffect(() => {
    if (view === 'applications' && jobIdFromUrl) {
      if (String(selectedJobId) !== String(jobIdFromUrl)) {
        setSelectedJobId(jobIdFromUrl);
      }
      if (viewingJobDescription) {
        setViewingJobDescription(null);
      }
      onViewChange?.(true);
      return;
    }

    // If URL doesn't point to applications, ensure we exit applications view.
    if (selectedJobId) {
      setSelectedJobId(null);
    }
    if (!viewingJobDescription) {
      onViewChange?.(false);
    }
  }, [view, jobIdFromUrl, selectedJobId, viewingJobDescription, onViewChange]);
  
  const handleViewApplications = (jobId) => {
    setSelectedJobId(jobId);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('view', 'applications');
      next.set('jobId', String(jobId));
      return next;
    });
    onViewChange?.(true); // Notify parent that we're viewing applications
  };
  
  const handleCloseApplications = () => {
    setSelectedJobId(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('view');
      next.delete('jobId');
      return next;
    });
    onViewChange?.(false); // Notify parent that we're back to overview
  };

  // AI Assistant page adapter: the stats cards' numbers (shared in-memory
  // cache filled by StatsCards) plus the job count. When a job's applicants
  // are open, ApplicationList registers on top of this.
  const assistantStats = useMemo(() => {
    const cached = getCachedOverviewStats();
    return {
      total_jobs: jobs.length,
      ...(cached?.stats || {}),
      ...(cached?.hiredStats ? { totalHired: cached.hiredStats.totalHired, recentHires: cached.hiredStats.recentHires } : {}),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs, jobsLoading]);
  useAssistantPage({ page: 'overview', stats: assistantStats, jobs: recentJobs, onOpenJob: handleViewApplications });

  const handleViewJobDescription = (jobId) => {
    const job = jobs.find((j) => (j.job_id || j.id) === jobId);
    setViewingJobDescription(job);
    onViewChange?.(true); // Notify parent that we're viewing job description
  };

  const handleCloseJobDescription = () => {
    setViewingJobDescription(null);
    onViewChange?.(false); // Notify parent that we're back to overview
  };

  const handleSaveJobDescription = () => {
    refreshJobs();
    setViewingJobDescription(null);
    onViewChange?.(false);
  };

  const handlePinJob = async (jobId) => {
    try {
      await togglePinJob(jobId);
    } catch (e) {
      console.error("Failed to pin/unpin job:", e);
      window.alert("Failed to pin/unpin job. Please try again.");
    }
  };

  const handleDeleteJob = async (jobId) => {
    const ok = window.confirm('Are you sure you want to delete this job?');
    if (!ok) return;

    try {
      await deleteJob(jobId);
    } catch (e) {
      console.error("Failed to delete job:", e);
      window.alert("Failed to delete job. Please try again.");
    }
  };
  
  if (viewingJobDescription) {
    return (
      <div className="flex flex-col w-full gap-6">
        <JobDescriptionView
          job={viewingJobDescription}
          onClose={handleCloseJobDescription}
          onSave={handleSaveJobDescription}
        />
      </div>
    );
  }
  
  return (
    <div className="flex flex-col w-full gap-8">
      {selectedJobId ? (
        <div>
          <ApplicationList jobId={selectedJobId} onBack={handleCloseApplications} />
        </div>
      ) : (
        <div className="flex flex-col gap-8">
          <StatsCards
            jobs={jobs}
            jobsLoading={jobsLoading}
            onRefreshJobs={refreshJobs}
          />
          <JobList
            jobs={recentJobs}
            jobsLoading={jobsLoading}
            onRefreshJobs={refreshJobs}
            handleViewApplications={handleViewApplications}
            onViewJobDescription={handleViewJobDescription}
            onPinJob={handlePinJob}
            onDeleteJob={handleDeleteJob}
          />
        </div>
      )}
    </div>
  );
}

export default JobDashboard;
